/**
 * The transfer panel (M10 P5a8b, placeholder UI, D-052): docked or moored beside another of your ships at the same
 * station (or with the carried pair), move cargo lots and locker modules between the flown ship and it, free.
 * Registered only while `moor.json transfers.enabled` is on (false until W6; `features/fleetPanels`), and it lists
 * nothing unless another of your ships is here (`transferModel` null). UI events only.
 */
import type { ReactNode } from 'react'
import type { M2Data } from '../../data/m2Schemas'
import { fail, type CmdResult } from '../../career/result'
import type { Career } from '../../career/types'
import { logger } from '../../core/logger'
import { placesAt, transferCargo, transferModule } from '../../fleet-link/transfer'
import type { FleetService } from '../../fleet-link/service'
import type { PanelContext, StationServicePanel } from '../../reach/ports'
import type { FleetView } from '../../strategic/types'
import { mountReact } from '../strategic/mount'
import { transferModel, type TransferModel, type TransferRow } from './models/transfer'

/** The panel order among the station tabs (just after the Hangar, 74). */
export const TRANSFER_PANEL_ORDER = 75

export interface TransferPanelDeps {
  fleet(): Pick<FleetService, 'fleetView'> | null
  career(): Career | null
  /** The Career writer (the runtime's `applyDevCommand`). */
  applyCareer(name: string, cmd: (career: Career) => Career): unknown
  modules: Pick<M2Data, 'modules'>
}

/** Runs one row's move on `career` (the rule's result: the next career or the refusal). */
export function runTransfer(career: Career, row: TransferRow, data: Pick<M2Data, 'modules'>, view: FleetView, stationId: string): CmdResult<Career> {
  const places = placesAt(career, stationId, view.ships)
  if (row.moduleId !== undefined) return transferModule(career, data, row.from, row.to, row.moduleId, places)
  if (row.lot !== undefined) return transferCargo(career, data, row.from, row.to, row.lot, places)
  return fail('invalidAmount', 'Nothing to move.', {})
}

function Rows(p: { rows: readonly TransferRow[]; verb: string; busy: boolean; onMove(row: TransferRow): void }): ReactNode {
  if (p.rows.length === 0) return <p className="sui-empty">Nothing to move.</p>
  return (
    <ul className="fp-list">
      {p.rows.map((r) => (
        <li key={r.key} className="fp-row" data-testid={`transfer-row-${r.key}`}>
          <span>{r.label}</span>
          <button type="button" className="sui-btn" disabled={p.busy} onClick={() => p.onMove(r)}>
            {p.verb}
          </button>
        </li>
      ))}
    </ul>
  )
}

export function TransferView(p: { model: TransferModel | null; status: string | null; busy: boolean; onMove(row: TransferRow): void }): ReactNode {
  return (
    <div className="sui fp-panel" data-testid="fleet-transfer">
      {p.status !== null && <p className="fp-status">{p.status}</p>}
      {p.model === null ? (
        <p className="sui-empty">No other ship of yours is here.</p>
      ) : (
        p.model.partners.map((s) => (
          <section key={s.id} data-testid={`transfer-ship-${s.id}`}>
            <h3>{s.name}</h3>
            <h4>Send to the {s.name}</h4>
            <Rows rows={s.send} verb="Send" busy={p.busy} onMove={p.onMove} />
            <h4>Take from the {s.name}</h4>
            <Rows rows={s.take} verb="Take" busy={p.busy} onMove={p.onMove} />
          </section>
        ))
      )}
    </div>
  )
}

export function createTransferPanel(deps: TransferPanelDeps): StationServicePanel {
  return {
    id: 'transfer',
    title: 'Transfers',
    order: TRANSFER_PANEL_ORDER,
    available: () => deps.fleet() !== null,
    mount(el: HTMLElement, ctx: PanelContext): () => void {
      const stationId = ctx.station.id
      const mount = mountReact(el, 'sui-mount')
      let view: FleetView | null = null
      let status: string | null = null
      let alive = true
      const render = (): void => {
        if (!alive) return
        const career = deps.career()
        const model =
          career === null || view === null
            ? null
            : transferModel(career, placesAt(career, stationId, view.ships), (id) => view?.ships.find((s) => s.id === id)?.name ?? id)
        mount.render(<TransferView model={model} status={status} busy={false} onMove={onMove} />)
        ctx.refresh()
      }
      function onMove(row: TransferRow): void {
        const career = deps.career()
        if (career === null || view === null) return
        const v = view
        const r = runTransfer(career, row, deps.modules, v, stationId)
        if (r.ok) deps.applyCareer('fleetTransfer', (c) => {
          const again = runTransfer(c, row, deps.modules, v, stationId)
          return again.ok ? again.value : c
        })
        status = r.ok ? `Moved ${row.label}.` : r.error.message
        render()
      }
      const f = deps.fleet()
      if (f !== null) {
        void f
          .fleetView()
          .then((v) => {
            view = v
            render()
          })
          .catch((err: unknown) => logger.warn('transfer panel: could not read the fleet view', err))
      }
      render()
      return (): void => {
        alive = false
        mount.unmount()
      }
    },
  }
}
